import useGenreOptions from "../hooks/useGenreOptions";
// src/pages/ArtistsIndex.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Avatar,
  Grid,
  Container,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  Rating,
  Snackbar,
  Alert,
  useMediaQuery,
  Drawer,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PriceChangeIcon from "@mui/icons-material/PriceChange";

// Import the bands data
// If your bundler doesn't support JSON imports, use fetch instead (see alternative below)
import {
  fetchArtists,
  getArtistLocationQuery,
  getAvailableCities,
} from "../services/artist.service";
import { buildRecipientInboxPath } from "../services/messaging";
import { FilterButton } from "../components/DiscoveryActions";
import CommittedRadiusSlider from "../components/CommittedRadiusSlider";
import { INFINITE_RADIUS, NEAR_ME_RADIUS_KM, radiusLabel } from "../services/radius";
import { formatMoneyRange, getMessage, useLocale } from "../i18n/locale";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import { getProfileImage, imageFallbackHandler, PROFILE_IMAGE_FALLBACK } from "../services/profileImages";
import useDebouncedSearch from "../hooks/useDebouncedSearch";

// ---------------- UI Components ----------------
function FeeChip({ min, max, currency }) {
  const localeContext = useLocale();
  if ((min === null || min === undefined || min === "") && (max === null || max === undefined || max === "")) return null;

  return (
    <Chip
      size="small"
      icon={<PriceChangeIcon />}
      label={formatMoneyRange(min, max, { locale: localeContext.locale, currency: currency || localeContext.currency })}
      variant="outlined"
    />
  );
}

function ArtistCard({ artist, onOpen, onContact }) {
  const { locale } = useLocale();
  const contactLabel = getMessage(locale, "venueContact");
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardActionArea onClick={() => onOpen?.(artist)} sx={{ flexGrow: 1 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={getProfileImage(artist)} imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }} alt={artist.name} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={800} noWrap>{artist.name}</Typography>
                <LinkedAccountBadge ownerUserId={artist.ownerUserId} />
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>
                {artist.city} • {artist.genres.join(", ")}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.5, flexWrap: "wrap" }}
              >
                {artist.rating && (
                  <>
                    <Rating
                      value={artist.rating || 0}
                      precision={0.1}
                      size="small"
                      readOnly
                      icon={<StarRoundedIcon fontSize="inherit" />}
                      emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {artist.rating?.toFixed?.(1) ?? "–"}
                    </Typography>
                  </>
                )}
                <FeeChip min={artist.feeMin} max={artist.feeMax} currency={artist.currency} />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button fullWidth variant="contained" onClick={() => onOpen?.(artist)}>
          View profile
        </Button>
        {onContact && <Tooltip title={contactLabel}>
          <span>
            <Button fullWidth variant="outlined" onClick={() => onContact?.(artist)}>
              {contactLabel}
            </Button>
          </span>
        </Tooltip>}
      </CardActions>
    </Card>
  );
}

// ---------------- Main Component ----------------
export default function ArtistsIndex({ onNavigate, onRequireAuth, onChangeRegion, discoveryLocation, discoveryRadiusKm, onChangeDiscoveryRadius, user, region = "Anywhere" }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const contactArtist = (artist) => {
    const redirectTo = buildRecipientInboxPath("artist", artist.id);
    if (!user) return onRequireAuth?.({ redirectTo, reasonKey: "loginModalMessageReason" });
    onNavigate?.(redirectTo);
  };

  const genreOptions = useGenreOptions();
  const [cityOptions, setCityOptions] = useState(["Anywhere"]);

  useEffect(() => {
    getAvailableCities().then(setCityOptions).catch(() => setCityOptions(["Anywhere"]));
  }, []);

  // Filters
  const { inputValue: searchInput, setInputValue: setSearchInput, searchValue: query, submitSearch } = useDebouncedSearch();
  const [genres, setGenres] = useState(["All"]);
  const [fee, setFee] = useState("any");
  const [ratingMin, setRatingMin] = useState(0);
  const [city, setCity] = useState(region);
  const radiusKm = discoveryRadiusKm;
  const setRadiusKm = onChangeDiscoveryRadius;

  // Data
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);
  const pageSize = 8;

  // UI
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const runFetch = async ({ reset = false } = {}) => {
    setLoading(true);
    const nextPage = reset ? 1 : page;
    const res = await fetchArtists({
      registeredOnly: true,
      query,
      genres,
      fee,
      ratingMin,
      radiusKm,
      ...getArtistLocationQuery(city, discoveryLocation, radiusKm),
      page: nextPage,
      pageSize,
    });
    setLoading(false);
    setHasMore(res.hasMore);
    if (reset) {
      setPage(1);
      setArtists(res.data);
    } else {
      setArtists((prev) => [...prev, ...res.data]);
    }
  };

  useEffect(() => {
    runFetch({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, JSON.stringify(genres), fee, ratingMin, city, radiusKm, discoveryLocation]);

  const loadNextPage = useCallback(async () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setLoading(true);
    try {
      const res = await fetchArtists({
        registeredOnly: true,
        query, genres, fee, ratingMin, radiusKm,
        ...getArtistLocationQuery(city, discoveryLocation, radiusKm),
        page: nextPage,
        pageSize,
      });
      setPage(nextPage);
      setHasMore(res.hasMore);
      setArtists((prev) => [...prev, ...res.data]);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, page, query, genres, fee, ratingMin, radiusKm, city, discoveryLocation]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && loadNextPage(),
      { rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadNextPage]);

  // Subtitle & active filter count
  const subtitle = useMemo(() => {
    const parts = [region];
    if (city !== "Anywhere") parts.push(city);
    parts.push(radiusLabel(radiusKm));
    if (genres.length && !genres.includes("All")) parts.push(genres.join(", "));
    if (fee !== "any")
      parts.push(
        fee === "lt1000" ? "< R1,000" : fee === "gt3000" ? "> R3,000" : "R1,000–R3,000"
      );
    if (ratingMin) parts.push(`≥ ${ratingMin.toFixed(1)}★`);
    return parts.join(" • ");
  }, [region, city, radiusKm, genres, fee, ratingMin]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (!(genres.length === 1 && genres[0] === "All")) n++;
    if (fee !== "any") n++;
    if (ratingMin) n++;
    if (city !== "Anywhere") n++;
    if (radiusKm !== INFINITE_RADIUS) n++;
    return n;
  }, [genres, fee, ratingMin, city, radiusKm]);

  const resetFilters = () => {
    setGenres(["All"]);
    setFee("any");
    setRatingMin(0);
    setCity("Anywhere");
    setRadiusKm(INFINITE_RADIUS);
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => onNavigate?.("/")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Artists
          </Typography>

          <Box component="form" onSubmit={submitSearch} sx={{ flex: 1, mx: 1 }}>
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search artists or bands"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Tooltip title="Filters">
            <FilterButton count={activeCount} onClick={() => setFiltersOpen(true)} />
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Chip
              icon={<LocationOnIcon />}
              label={city}
              onClick={async () => {
                if (city === "Near me" && radiusKm !== INFINITE_RADIUS) {
                  setRadiusKm(INFINITE_RADIUS);
                  return;
                }
                if (discoveryLocation) {
                  setCity("Near me");
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                  return;
                }
                const location = await onChangeRegion?.();
                if (location) {
                  setCity("Near me");
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                }
              }}
              variant={city === "Near me" && radiusKm !== INFINITE_RADIUS ? "filled" : "outlined"}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {subtitle ? `Showing: ${subtitle}` : "Showing all artists"}
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {(loading && artists.length === 0 ? Array.from({ length: 8 }) : artists).map(
            (a, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={a?.id ?? idx}>
                {a ? (
                  <ArtistCard
                    artist={a}
                    onOpen={(artist) => onNavigate?.(`/artists/${artist.id}`)}
                    onContact={contactArtist}
                  />
                ) : (
                  <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3 }} />
                )}
              </Grid>
            )
          )}
        </Grid>

        <Stack ref={loadMoreRef} alignItems="center" sx={{ py: 3 }}>
          {hasMore ? (
            <Button
              size="large"
              disabled={loading}
              onClick={loadNextPage}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You've reached the end.
            </Typography>
          )}
        </Stack>
      </Container>

      <Drawer
        anchor={isMdUp ? "right" : "bottom"}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { md: 360 },
            borderTopLeftRadius: { xs: 16, md: 0 },
            borderTopRightRadius: { xs: 16, md: 0 },
            p: 2,
            gap: 2,
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            Filters
          </Typography>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Genres
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={genreOptions}
              value={genres.includes("All") ? [] : genres}
              onChange={(_, values) => {
                if (!values?.length) return setGenres(["All"]);
                if (values.includes("All")) return setGenres(["All"]);
                setGenres(values);
              }}
              renderInput={(params) => <TextField {...params} placeholder="Select genres" />}
              renderTags={(value, getTagProps) =>
                value.slice(0, 3).map((option, index) => (
                  <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                ))
              }
            />
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => setGenres(["All"])}>
              Clear genres
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Typical fee
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={fee}
              onChange={(_, v) => v && setFee(v)}
              sx={{ flexWrap: "wrap" }}
            >
              <ToggleButton value="any">Any</ToggleButton>
              <ToggleButton value="lt1000">&lt; R1,000</ToggleButton>
              <ToggleButton value="1000-3000">R1,000–R3,000</ToggleButton>
              <ToggleButton value="gt3000">&gt; R3,000</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Minimum rating
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Rating
                value={ratingMin}
                precision={0.5}
                max={5}
                onChange={(_, v) => setRatingMin(v || 0)}
              />
              <Typography variant="body2" color="text.secondary">
                {ratingMin ? `≥ ${ratingMin.toFixed(1)}★` : "Any"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {[0, 3.5, 4.0, 4.5].map((r) => (
                <Chip
                  key={r}
                  icon={<StarRoundedIcon />}
                  label={r === 0 ? "All ratings" : `≥ ${r}`}
                  variant={ratingMin === r ? "filled" : "outlined"}
                  onClick={() => setRatingMin(r)}
                  size="small"
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              City
            </Typography>
            <Autocomplete
              size="small"
              options={cityOptions}
              value={city}
              onChange={(_, value) => setCity(value || "Anywhere")}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select city" />
              )}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Radius: {radiusLabel(radiusKm)}
            </Typography>
            <CommittedRadiusSlider value={radiusKm} onChange={setRadiusKm} />
          </Box>

          <Divider />

          <Stack direction="row" justifyContent="space-between" gap={1}>
            <Button onClick={resetFilters}>Reset</Button>
            <Button
              variant="contained"
              onClick={() => setFiltersOpen(false)}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      <Box sx={{ height: 16 }} />
    </Box>
  );
}
